import React, { useState, useEffect, useRef } from 'react';
import { B } from '../constants';
import { promos as promosAPI } from '../api';

const STATUS_ICONS = {
  READY: '✅',
  COMPLETED: '✅',
  PROCESSING: '⏳',
  PENDING: '⏸️',
  FAILED: '❌',
  CANCELLED: '🚫',
};

const STATUS_COLORS = {
  READY: B.forest,
  COMPLETED: B.forest,
  PROCESSING: B.amber,
  PENDING: B.muted,
  FAILED: '#dc3545',
  CANCELLED: '#999',
};

export default function BatchProgress({ batchId, onClose, onRetry }) {
  const [batch, setBatch] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!batchId) return;
    const fetchStatus = async () => {
      try {
        const data = await promosAPI.getBatchStatus(batchId);
        setBatch(data);
        if (data.status === 'COMPLETED' || data.status === 'FAILED' || data.status === 'CANCELLED') {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
      }
    };
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [batchId]);

  if (!batch) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        onClick={e => e.target === e.currentTarget && onClose?.()}>
        <div style={{ background: 'white', borderRadius: 24, padding: 40, textAlign: 'center' }}>
          <p style={{ fontFamily: "'Montserrat',sans-serif", color: B.muted }}>Loading batch status...</p>
        </div>
      </div>
    );
  }

  const doneCount = batch.completedVideos + batch.failedVideos;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: 520, maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: B.night, margin: 0 }}>
              📦 Batch Progress
            </h2>
            <span onClick={onClose} style={{ background: '#f0f0f0', border: 'none', color: B.muted, width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</span>
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: B.muted }}>
                {batch.status === 'COMPLETED' ? 'All done!' : batch.status === 'FAILED' ? 'Some videos failed' : 'Processing...'}
              </span>
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: B.night }}>
                {doneCount}/{batch.totalVideos}
              </span>
            </div>
            <div style={{ height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${batch.progress}%`,
                background: batch.failedVideos > 0 && batch.status === 'FAILED'
                  ? `linear-gradient(90deg, ${B.forest}, #dc3545)`
                  : batch.status === 'COMPLETED' ? B.forest : `linear-gradient(90deg, ${B.forest}, ${B.amber})`,
                borderRadius: 4, transition: 'width 0.5s ease',
              }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: '#f9f9f9', borderRadius: 10 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: B.forest }}>{batch.completedVideos}</div>
              <div style={{ fontSize: 11, color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>Completed</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: '#f9f9f9', borderRadius: 10 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: B.amber }}>{batch.totalVideos - doneCount}</div>
              <div style={{ fontSize: 11, color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>Processing</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: '#f9f9f9', borderRadius: 10 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#dc3545' }}>{batch.failedVideos}</div>
              <div style={{ fontSize: 11, color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>Failed</div>
            </div>
          </div>

          <div>
            <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: B.night, marginBottom: 8 }}>
              Videos
            </p>
            {(batch.videos || []).map(v => (
              <div key={v.promoId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: '#f9f9f9', marginBottom: 4 }}>
                <span style={{ fontSize: 16 }}>{STATUS_ICONS[v.status] || '⏸️'}</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.night }}>{v.title}</span>
                  {v.error && <p style={{ fontSize: 11, color: '#dc3545', margin: 0 }}>{v.error}</p>}
                </div>
                <span style={{ fontSize: 11, color: STATUS_COLORS[v.status] || B.muted, fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>
                  {v.status}
                </span>
              </div>
            ))}
          </div>

          {batch.failedVideos > 0 && onRetry && (
            <button onClick={() => onRetry(batchId)}
              style={{ width: '100%', marginTop: 16, padding: '12px', borderRadius: 10, border: 'none', background: B.forest, color: 'white', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700 }}>
              🔄 Retry Failed
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
